import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ArrowLeft, Save, CheckCircle, AlertCircle, Eye, EyeOff, TestTube, Database, Shield, HardDrive, FileText, Cloud, Crown, User, ChevronDown, LogOut } from 'lucide-react';
import { toast } from 'sonner';

// Using relative URLs
const API = '/api';

const S3Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  
  const [formData, setFormData] = useState({
    storageType: 's3',
    region: 'us-east-1',
    accountId: '',  // For R2
    accessKeyId: '',
    secretAccessKey: '',
    bucketName: '',
    maxFileSize: 104857600,
    allowedExtensions: 'pdf,jpg,jpeg,png,doc,docx,xls,xlsx,mp4,mov,avi'
  });
  
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/r2-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        storageType: response.data.storage_type || 's3',
        region: response.data.region || 'us-east-1',
        accountId: response.data.account_id || '',
        accessKeyId: '',  // Never send secrets back
        secretAccessKey: '',
        bucketName: response.data.bucket_name || '',
        maxFileSize: response.data.max_file_size || 104857600,
        allowedExtensions: response.data.allowed_extensions || 'pdf,jpg,jpeg,png,doc,docx,xls,xlsx,mp4,mov,avi'
      });
      setIsConfigured(response.data.is_configured);
    } catch (error) {
      console.error('Failed to load configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStorageTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      storageType: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const token = localStorage.getItem('token');
      
      // Prepare payload
      const payload = {
        storage_type: formData.storageType,
        bucket_name: formData.bucketName,
        max_file_size: parseInt(formData.maxFileSize),
        allowed_extensions: formData.allowedExtensions
      };
      
      // Add storage-specific fields
      if (formData.storageType === 's3') {
        payload.region = formData.region;
      } else if (formData.storageType === 'r2') {
        if (formData.accountId) {
          payload.account_id = formData.accountId;
        }
      }
      
      // Only include credentials if provided
      if (formData.accessKeyId) {
        payload.access_key_id = formData.accessKeyId;
      }
      if (formData.secretAccessKey) {
        payload.secret_access_key = formData.secretAccessKey;
      }
      
      const response = await axios.post(`${API}/admin/r2-config`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Configuration saved successfully');
        setIsConfigured(true);
        // Clear secret fields after successful save
        setFormData(prev => ({
          ...prev,
          accessKeyId: '',
          secretAccessKey: ''
        }));
        
        // Refresh to get latest config
        await fetchConfiguration();
      } else {
        toast.warning(response.data.message || 'Configuration saved with warnings');
      }
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save configuration';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/r2-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Connection successful!');
      } else {
        toast.error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Connection test failed';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  const regions = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'Europe (Ireland)' },
    { value: 'eu-west-2', label: 'Europe (London)' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-6">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Cloud className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">File Storage</h1>
                  <p className="text-sm text-slate-600">Configure AWS S3 or Cloudflare R2</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Status Banner */}
        {isConfigured && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-green-900 font-medium">Storage Configured</p>
              <p className="text-green-700 text-sm">File uploads are enabled across the portal</p>
            </div>
          </div>
        )}

        {/* Configuration Form */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <CardTitle>Storage Credentials</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing || !formData.bucketName}
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Storage Type Selection */}
            <div>
              <Label htmlFor="storageType">Storage Provider *</Label>
              <Select value={formData.storageType} onValueChange={handleStorageTypeChange}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select storage provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="s3">AWS S3 (Recommended)</SelectItem>
                  <SelectItem value="r2">Cloudflare R2</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {formData.storageType === 's3' ? 'Industry standard, reliable CORS support' : 'Cost-effective, but complex CORS setup'}
              </p>
            </div>

            {/* AWS S3 Specific Fields */}
            {formData.storageType === 's3' && (
              <div>
                <Label htmlFor="region">AWS Region *</Label>
                <Select value={formData.region} onValueChange={(value) => setFormData(prev => ({...prev, region: value}))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map(region => (
                      <SelectItem key={region.value} value={region.value}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Choose the region where you created your S3 bucket
                </p>
              </div>
            )}

            {/* Cloudflare R2 Specific Fields */}
            {formData.storageType === 'r2' && (
              <div>
                <Label htmlFor="accountId">Account ID *</Label>
                <Input
                  id="accountId"
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleInputChange}
                  placeholder="Found in Cloudflare Dashboard → R2 → Overview"
                  className="mt-2 font-mono text-sm"
                />
              </div>
            )}

            {/* Access Key ID */}
            <div>
              <Label htmlFor="accessKeyId">Access Key ID *</Label>
              <Input
                id="accessKeyId"
                name="accessKeyId"
                value={formData.accessKeyId}
                onChange={handleInputChange}
                placeholder={formData.storageType === 's3' ? 'AWS IAM Access Key ID' : 'R2 API Token Access Key ID'}
                className="mt-2 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave blank to keep existing key
              </p>
            </div>

            {/* Secret Access Key */}
            <div>
              <Label htmlFor="secretAccessKey">Secret Access Key *</Label>
              <div className="relative mt-2">
                <Input
                  id="secretAccessKey"
                  name="secretAccessKey"
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.secretAccessKey}
                  onChange={handleInputChange}
                  placeholder="••••••••••••••••••••••••••••••••••••••••"
                  className="font-mono text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Leave blank to keep existing key
              </p>
            </div>

            {/* Bucket Name */}
            <div>
              <Label htmlFor="bucketName">Bucket Name *</Label>
              <Input
                id="bucketName"
                name="bucketName"
                value={formData.bucketName}
                onChange={handleInputChange}
                placeholder="ethira-audit-evidence"
                className="mt-2"
              />
            </div>

            {/* Advanced Settings */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <HardDrive className="w-4 h-4" />
                Advanced Settings
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maxFileSize">Max File Size (bytes)</Label>
                  <Input
                    id="maxFileSize"
                    name="maxFileSize"
                    type="number"
                    value={formData.maxFileSize}
                    onChange={handleInputChange}
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Current: {(formData.maxFileSize / 1024 / 1024).toFixed(0)}MB
                  </p>
                </div>

                <div>
                  <Label htmlFor="allowedExtensions">Allowed Extensions</Label>
                  <Input
                    id="allowedExtensions"
                    name="allowedExtensions"
                    value={formData.allowedExtensions}
                    onChange={handleInputChange}
                    placeholder="pdf,jpg,png,doc,docx"
                    className="mt-2 font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSave}
                disabled={saving || !formData.bucketName}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Setup Instructions:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  {formData.storageType === 's3' ? (
                    <>
                      <li>Create an S3 bucket in your AWS account</li>
                      <li>Configure CORS policy to allow your domain</li>
                      <li>Create IAM user with S3 access permissions</li>
                      <li>Generate access keys from IAM console</li>
                    </>
                  ) : (
                    <>
                      <li>Create an R2 bucket in Cloudflare</li>
                      <li>Configure CORS with exact origin URLs</li>
                      <li>Generate R2 API tokens</li>
                      <li>Copy Account ID from R2 Overview</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default S3Settings;